
import { 
  Customer, Subscription, Invoice, AIUsageLog, CreditWallet, 
  PlanDetail, Coupon, MetricData, ChartData, MRRMovement, SubscriptionStatus, AbandonedCart,
  RevenueAnomaly, ActivityEvent
} from '../types';
import * as mock from './mockData';

const shouldUseMockFallback = () => {
  return false;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('subscale_auth_token') || '';
  return { 'X-API-Key': token, 'Authorization': `Bearer ${token}` };
};

const request = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const url = endpoint;
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...options.headers,
  };

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      if (!endpoint.includes('/auth/login') && !endpoint.includes('/auth/magic-verify')) {
        localStorage.removeItem('subscale_auth_token');
        window.dispatchEvent(new CustomEvent('subscale_auth_change', { detail: { authenticated: false } }));
      }
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API Error: ${response.statusText}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  } catch (error) {
    console.warn(`API Request Failed: ${url}. Proceeding with potential local fallback if applicable.`, error);
    throw error;
  }
};

const requestBlob = async (endpoint: string, options: RequestInit = {}): Promise<Blob> => {
  const url = endpoint;
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  try {
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      localStorage.removeItem('subscale_auth_token');
      window.location.reload();
      throw new Error('Unauthorized');
    }
    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);
    return response.blob();
  } catch (error) {
    console.error(`API Blob Request Failed: ${url}`, error);
    throw error;
  }
};

const ensureArray = <T>(data: any): T[] => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

export const api = {
  auth: {
    login: async (email: string, password: string) => {
      try {
        const data = await request<any>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });
        const token = data.token;
        const user = data.user || { id: data._id, email: data.email || email, name: data.name || 'Admin', role: 'Super Admin' };
        localStorage.setItem('subscale_auth_token', token);
        return { token, user };
      } catch (e) {
        throw new Error('System Access Denied');
      }
    },
    verifyLoginToken: async (token: string, customerId: string) => {
      const res = await fetch('/api/auth/magic-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        throw new Error('Magic link invalid or expired');
      }

      const data = await res.json();
      const sessionToken = data.token;

      if (!sessionToken) {
        throw new Error('No customer session token returned');
      }

      localStorage.setItem('subscale_auth_token', sessionToken);

      return {
        id: data.user?.customerId || customerId,
        customerId: data.user?.customerId || customerId,
        email: data.user?.email,
        role: data.user?.role || 'customer',
      };
    },
    logout: async () => {
      try {
        await request('/api/auth/logout', { method: 'POST' });
      } catch (e) {}
      localStorage.removeItem('subscale_auth_token');
    },
    isAuthenticated: () => !!localStorage.getItem('subscale_auth_token'),
    me: async () => {
      return await request<any>('/api/auth/me');
    },
    getTokens: async () => {
      const res = await request<any>('/api/v1/auth/tokens');
      return ensureArray(res);
    },
    generateToken: async (name: string) => {
      const res = await request<any>('/api/v1/auth/tokens', { 
        method: 'POST', 
        body: JSON.stringify({ name: name || 'API Token' }) 
      });
      return res;
    },
    revokeToken: async (id: string) => {
      await request<void>(`/api/v1/auth/tokens/${id}`, { method: 'DELETE' });
    },
  },

  customers: {
    getAll: async () => {
      try {
        const res = await request<any>('/api/v1/customers');
        const data = ensureArray<Customer>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.mockCustomers;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockCustomers;
        throw e;
      }
    },
    getOne: async (id: string) => {
      try {
        return await request<Customer>(`/api/v1/customers/${id}`);
      } catch (e) {
        if (shouldUseMockFallback()) {
          return mock.mockCustomers.find(c => c.id === id) || mock.mockCustomers[0];
        }
        throw e;
      }
    },
    create: (data: Customer) => request<Customer>('/api/v1/customers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<Customer>) => request<Customer>(`/api/v1/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    manageCredits: (id: string, amount: number, action: 'add' | 'remove') => 
      request<Customer>(`/api/v1/customers/${id}/credits`, { 
        method: 'PATCH', 
        body: JSON.stringify({ amount, action }) 
      }),
    delete: (id: string) => request<void>(`/api/v1/customers/${id}`, { method: 'DELETE' }),
  },

  abandonment: {
    track: (data: Partial<AbandonedCart>) => request<AbandonedCart>('/api/v1/abandonment/track', { method: 'POST', body: JSON.stringify(data) }),
    getAll: async () => {
      try {
        const res = await request<any>('/api/v1/abandonment');
        const data = ensureArray<AbandonedCart>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.mockAbandonedCarts;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockAbandonedCarts;
        throw e;
      }
    },
    recoverCart: (id: string) => request<void>(`/api/v1/abandonment/${id}/recover`, { method: 'POST' }),
  },

  subscriptions: {
    getAll: async () => {
      try {
        const res = await request<any>('/api/v1/subscriptions');
        const data = ensureArray<Subscription>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.mockSubscriptions;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockSubscriptions;
        throw e;
      }
    },
    create: (data: Subscription) => request<Subscription>('/api/v1/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
    update: async (id: string, data: Partial<Subscription>) => {
      try {
        if (data.status) {
          return await request<Subscription>(`/api/v1/subscriptions/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: data.status, autoRenew: data.autoRenew })
          });
        }
        if (data.plan) {
          return await request<Subscription>(`/api/v1/subscriptions/${id}/plan`, {
            method: 'PATCH',
            body: JSON.stringify({ newPlanId: data.plan }) 
          });
        }
        return await request<Subscription>(`/api/v1/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockSubscriptions[0];
        throw e;
      }
    },
    delete: (id: string) => request<void>(`/api/v1/subscriptions/${id}`, { method: 'DELETE' }),
  },

  invoices: {
    getAll: async () => {
      try {
        const res = await request<any>('/api/v1/invoices');
        const data = ensureArray<Invoice>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.mockInvoices;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockInvoices;
        throw e;
      }
    },
    create: (data: Invoice) => request<Invoice>('/api/v1/invoices', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: string) => request<void>(`/api/v1/invoices/${id}`, { method: 'DELETE' }),
    getPdf: (id: string) => requestBlob(`/api/v1/invoices/${id}/pdf`),
  },

  plans: {
    getAll: async () => {
      try {
        const res = await request<any>('/api/v1/plans');
        const data = ensureArray<PlanDetail>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.INITIAL_PLANS;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.INITIAL_PLANS;
        throw e;
      }
    },
    create: (data: PlanDetail) => request<PlanDetail>('/api/v1/plans', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<PlanDetail>) => request<PlanDetail>(`/api/v1/plans/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    toggleStatus: (id: string) => request<void>(`/api/v1/plans/${id}/toggle`, { method: 'PATCH' }),
    delete: (id: string) => request<void>(`/api/v1/plans/${id}`, { method: 'DELETE' }),
  },

  coupons: {
    getAll: async () => {
      try {
        const res = await request<any>('/api/v1/coupons');
        const data = ensureArray<Coupon>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.mockCoupons;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockCoupons;
        throw e;
      }
    },
    create: (data: Coupon) => request<Coupon>('/api/v1/coupons', { method: 'POST', body: JSON.stringify(data) }),
    validate: (code: string, cartAmount: number, customerId: string) => 
      request<any>('/api/v1/coupons/validate', { method: 'POST', body: JSON.stringify({ code, cartAmount, customerId }) }),
    delete: (id: string) => request<void>(`/api/v1/coupons/${id}`, { method: 'DELETE' }),
  },

  wallet: {
    get: () => request<CreditWallet>('/api/v1/wallet').catch((e) => {
      if (shouldUseMockFallback()) return mock.mockWallet;
      throw e;
    }),
    update: (data: Partial<CreditWallet>) => request<CreditWallet>('/api/v1/wallet', { method: 'PATCH', body: JSON.stringify(data) }).catch((e) => {
      if (shouldUseMockFallback()) return mock.mockWallet;
      throw e;
    }),
  },

  usage: {
    getLogs: async () => {
      try {
        const res = await request<any>('/api/v1/usage/logs');
        const data = ensureArray<AIUsageLog>(res);
        if (data.length === 0 && shouldUseMockFallback()) return mock.mockAIUsageLogs;
        return data;
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockAIUsageLogs;
        throw e;
      }
    },
    track: (data: any) => request<void>('/api/v1/usage/track', { method: 'POST', body: JSON.stringify(data) }),
  },

  events: {
    getDefinitions: async () => {
      try {
        const res = await request<any>('/api/v1/events/definitions');
        return ensureArray<any>(res);
      } catch (e) {
        if (shouldUseMockFallback()) return [];
        throw e;
      }
    },
    createDefinition: (data: any) => request<any>('/api/v1/events/definitions', { method: 'POST', body: JSON.stringify(data) }),
    getLogs: async () => {
      try {
        const res = await request<any>('/api/v1/events/logs');
        return ensureArray<any>(res);
      } catch (e) {
        if (shouldUseMockFallback()) return [];
        throw e;
      }
    }
  },

  system: {
    getMode: () => request<{ mode: 'live' | 'demo'; isLive: boolean; totalCustomers: number; totalSubscriptions: number; totalInvoices: number; hasDemoData: boolean }>('/api/v1/system/mode').catch(() => ({ mode: 'live' as const, isLive: true, totalCustomers: 0, totalSubscriptions: 0, totalInvoices: 0, hasDemoData: false })),
    clearDemoData: (options?: { wipeAll?: boolean }) => request<{ status: string; message: string; mode: string; totalCustomers: number; totalSubscriptions: number; totalInvoices: number; totalCarts: number }>('/api/v1/system/clear-demo-data', { method: 'POST', body: JSON.stringify(options || {}) }),
    loadDemoData: () => request<{ status: string; message: string; mode: string; totalCustomers: number; totalSubscriptions: number; totalInvoices: number }>('/api/v1/system/load-demo-data', { method: 'POST' }),
  },

  analytics: {
    getDashboard: async (date?: string) => {
      try {
        const query = date ? `?date=${date}` : '';
        return await request<MetricData>(`/api/v1/analytics/dashboard${query}`);
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockMetrics;
        throw e;
      }
    },
    getMrrMovement: async () => {
      try {
        const res = await request<any>('/api/v1/analytics/mrr-movement');
        return ensureArray<MRRMovement>(res);
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockMRRMovement;
        throw e;
      }
    },
    getRevenueChart: async (date?: string) => {
      try {
        const query = date ? `?date=${date}` : '';
        const res = await request<any>(`/api/v1/analytics/revenue-chart${query}`);
        return ensureArray<ChartData>(res);
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockChartData;
        throw e;
      }
    },
    getAnomalies: async () => {
      try {
        const res = await request<any>('/api/v1/analytics/anomalies');
        return ensureArray<RevenueAnomaly>(res);
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockAnomalies;
        throw e;
      }
    },
    getActivityPulse: async () => {
      try {
        const res = await request<any>('/api/v1/analytics/activity-pulse');
        return ensureArray<ActivityEvent>(res);
      } catch (e) {
        if (shouldUseMockFallback()) return mock.mockActivityPulse;
        throw e;
      }
    },
    getLifecycle: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      return await request<any>(`/api/v1/analytics/lifecycle${q}`);
    },
    getCohorts: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      const res = await request<any[]>(`/api/v1/analytics/cohorts${q}`);
      return ensureArray(res);
    },
    getUnitEconomics: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      return await request<any>(`/api/v1/analytics/unit-economics${q}`);
    },
    getTrialFunnel: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      const res = await request<any[]>(`/api/v1/analytics/trial-funnel${q}`);
      return ensureArray(res);
    },
    getGeographic: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      const res = await request<any[]>(`/api/v1/analytics/geographic${q}`);
      return ensureArray(res);
    },
    getStickiness: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      return await request<any>(`/api/v1/analytics/stickiness${q}`);
    },
    getArpuTrend: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      const res = await request<any[]>(`/api/v1/analytics/arpu-trend${q}`);
      return ensureArray(res);
    },
    getNrrTrend: async (range?: string) => {
      const q = range ? `?range=${range}` : '';
      const res = await request<any[]>(`/api/v1/analytics/nrr-trend${q}`);
      return ensureArray(res);
    },
    getGrowth: async () => {
      return await request<any>('/api/v1/analytics/growth');
    },
  },

  settings: {
    get: () => request<any>('/api/v1/settings').catch(() => ({})),
    getPublic: () => request<any>('/api/v1/settings/public').catch(() => ({})),
    update: (data: any) => request<any>('/api/v1/settings', { method: 'PUT', body: JSON.stringify(data) }),
    updateKeys: (keys: any) => request<any>('/api/v1/settings/keys', { method: 'PUT', body: JSON.stringify(keys) }),
  },
  
  paymentGateways: {
    configure: (gateway: 'paddle' | 'cashfree' | 'lemonsqueezy', config: any) => 
      request<void>(`/api/v1/gateways/${gateway}/configure`, { method: 'POST', body: JSON.stringify(config) }),
    getStatus: (gateway: 'paddle' | 'cashfree' | 'lemonsqueezy') => 
      request<{ enabled: boolean }>(`/api/v1/gateways/${gateway}/status`),
  },
  
  checkout: {
    createRazorpaySession: (data: any) =>
      request<any>('/api/v1/checkout/razorpay', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getSession: (sessionId: string) =>
      request<any>(`/api/v1/checkout/session/${sessionId}`),
  },
  
  paddle: {
    getSubscriptions: () => request<any[]>('/api/v1/paddle/subscriptions'),
  },
  
  cashfree: {
    getSubscriptions: () => request<any[]>('/api/v1/cashfree/subscriptions'),
  },
  
  mongodb: {
    databases: {
      list: () => request<any[]>('/api/databases'),
      get: (dbName: string) => request<any>(`/api/databases/${dbName}`),
      create: (dbName: string, data: any) => request<any>(`/api/databases/${dbName}`, { method: 'POST', body: JSON.stringify(data) }),
      delete: (dbName: string) => request<void>(`/api/databases/${dbName}`, { method: 'DELETE' }),
    },
    collections: {
      list: (dbName: string) => request<any[]>(`/api/collections/${dbName}`),
      get: (dbName: string, collectionName: string) => request<any>(`/api/collections/${dbName}/${collectionName}`),
      create: (dbName: string, collectionName: string) => request<any>(`/api/collections/${dbName}/${collectionName}`, { method: 'POST' }),
      rename: (dbName: string, collectionName: string, newName: string) => request<any>(`/api/collections/${dbName}/${collectionName}`, { method: 'PUT', body: JSON.stringify({ newName }) }),
      delete: (dbName: string, collectionName: string) => request<void>(`/api/collections/${dbName}/${collectionName}`, { method: 'DELETE' }),
    },
    documents: {
      list: (dbName: string, collectionName: string, params: any = {}) => {
        const query = new URLSearchParams(params).toString();
        return request<any>(`/api/documents/${dbName}/${collectionName}${query ? `?${query}` : ''}`);
      },
      get: (dbName: string, collectionName: string, id: string) => request<any>(`/api/documents/${dbName}/${collectionName}/${id}`),
      create: (dbName: string, collectionName: string, data: any) => request<any>(`/api/documents/${dbName}/${collectionName}`, { method: 'POST', body: JSON.stringify(data) }),
      bulkCreate: (dbName: string, collectionName: string, data: any[]) => request<any>(`/api/documents/${dbName}/${collectionName}/bulk`, { method: 'POST', body: JSON.stringify(data) }),
      replace: (dbName: string, collectionName: string, id: string, data: any) => request<any>(`/api/documents/${dbName}/${collectionName}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      update: (dbName: string, collectionName: string, id: string, data: any) => request<any>(`/api/documents/${dbName}/${collectionName}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: (id: string, dbName: string, collectionName: string) => request<void>(`/api/documents/${dbName}/${collectionName}/${id}`, { method: 'DELETE' }),
      deleteMany: (dbName: string, collectionName: string, filter: any) => request<void>(`/api/documents/${dbName}/${collectionName}`, { method: 'DELETE', body: JSON.stringify({ filter }) }),
    }
  }
};
